import { createRoute, redirect } from "@tanstack/react-router";
import { ErrorScreen } from "@/lib/Components/Screens/ErrorScreen";
import { fetchQuery } from "@/lib/GraphQLCodegen/fetcher";
import { globalSupplierSelectionList } from "@/app/Suppliers/GraphQL/globalSupplierSelectionList";
import { SUPPLIER_LIST_VARIABLES } from "@/app/Suppliers/Utils/globalSupplier";
import { SelectSupplierScreen } from "@/app/Suppliers/Screens/SelectSupplierScreen";
import { hasOrgsLayout } from "@/app/Auth/Routes/loginRoutes";
import { resolveOrgScope } from "@/routes/scope";

/**
 * Bare org URL `/$tenant/$orgId` — the supplier picker. Org-switch lands here so
 * the user chooses a supplier before entering the scoped tree. A sole supplier
 * auto-selects (no point showing a one-item picker); an empty org goes to
 * onboarding. `/$tenant/$orgId/supplier` (with the segment) stays the separate
 * "all suppliers" dashboard.
 */
export const selectSupplierRoute = createRoute({
  getParentRoute: () => hasOrgsLayout,
  path: "/$tenant/$orgId",
  errorComponent: ErrorScreen,
  beforeLoad: async ({ params, location }) => {
    const { tenant, orgId } = params as { tenant: string; orgId: string };

    const activeOrganisation = await resolveOrgScope(tenant, orgId, location);
    if (!activeOrganisation) {
      return;
    }

    const suppliers = await fetchQuery(
      globalSupplierSelectionList,
      SUPPLIER_LIST_VARIABLES,
      orgId,
    )
      .then((data) => data.suppliers.data)
      .catch(() => null);

    if (!suppliers) {
      return;
    }

    // No suppliers yet → non-admins finish setup in onboarding; admin orgs
    // legitimately have none, so render an empty picker rather than push them in.
    if (suppliers.length === 0) {
      if (!activeOrganisation.is_admin) {
        throw redirect({
          to: "/org/$orgId/create-supplier",
          params: { orgId },
        });
      }
      return;
    }

    // Exactly one supplier → skip the picker and drop straight into its scope.
    if (suppliers.length === 1) {
      throw redirect({
        href: `/${tenant}/${orgId}/supplier/${suppliers[0].id}`,
      });
    }

    // More than one → fall through and render the picker.
  },
  component: SelectSupplierScreen,
});
